import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  AlertTriangle,
  Building2,
  MoreHorizontal,
  Pencil,
  Plus,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

import type { ListB2BPatients } from '@virtual-care/client';
import {
  displayName,
  formatPatientMid,
  quotaSeverity,
} from '@virtual-care/isomorphic';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import { SearchInput, TableRowSort, client } from '@virtual-care/ui';

import { emailColumn, phoneNumberColumn } from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { CreateB2BPatient } from './create-b2b-patient.tsx';
import { EditB2BPatient } from './edit-b2b-patient.tsx';

export default function BtoBPatientsPage() {
  return (
    <div className="w-full">
      <B2BPatientsTable />
    </div>
  );
}

function B2BPatientsTable() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editPatient, setEditPatient] = useState<
    ListB2BPatients['records'][number] | null
  >(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useQuery({
    queryKey: ['b2b-patients', pagination, sorting, searchQuery],
    queryFn: () => {
      return client.request('GET /b2b/patients', {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        query: searchQuery || undefined,
        sortBy: (sorting.length > 0 ? sorting[0].id : undefined) as any,
        sortOrder:
          sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
      });
    },
    placeholderData: keepPreviousData,
  });

  const columns: ColumnDef<ListB2BPatients['records'][number]>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {displayName({ b2bProfile: row.original.profile })}
          </div>
        );
      },
    },
    emailColumn(),
    phoneNumberColumn(),
    {
      accessorKey: 'usageLimit',
      header: 'Usage limit',
      cell: ({ row }) => <QuotaCell count={row.original.profile.usageLimit} />,
    },
    {
      accessorKey: 'printingUsageLimit',
      header: '3D Printing',
      cell: ({ row }) => (
        <QuotaCell count={row.original.profile.printingUsageLimit ?? 0} />
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const patient = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setEditPatient(patient)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit quotas & access
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  navigator.clipboard.writeText(formatPatientMid(patient.mid))
                }
              >
                Copy MID ({formatPatientMid(patient.mid)})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <>
      <CountablePageHeader
        title="B2B Patients"
        totalCount={`${dataQuery.data?.totalCount ?? 0} total B2B patients`}
        icon={Building2}
      >
        <CreateB2BPatient
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSave={() => dataQuery.refetch()}
          trigger={
            <Button>
              <Plus className="mr-2 size-4" />
              Add B2B Patient
            </Button>
          }
        />
      </CountablePageHeader>

      <div className="mb-4">
        <SearchInput
          placeholder="Search B2B patients by name or email..."
          value={searchQuery}
          onChange={setSearchQuery}
          isLoading={dataQuery.isFetching}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      {editPatient && (
        <EditB2BPatient
          patient={editPatient}
          open={!!editPatient}
          onOpenChange={(isOpen) => !isOpen && setEditPatient(null)}
          onSaved={() => dataQuery.refetch()}
        />
      )}
    </>
  );
}

function QuotaCell({ count }: { count: number }) {
  const severity = quotaSeverity(count);
  if (severity === 'exhausted') {
    return (
      <div className="flex items-center gap-1 text-sm font-medium text-red-600">
        <XCircle className="h-4 w-4" />
        {count}
      </div>
    );
  }
  if (severity === 'low') {
    return (
      <div className="flex items-center gap-1 text-sm font-medium text-amber-600">
        <AlertTriangle className="h-4 w-4" />
        {count}
      </div>
    );
  }
  return <div className="text-muted-foreground text-sm">{count}</div>;
}
